<template>
    <div
        class="transform-hero section-wrapper"
        :class="viewport.is"
    >
        <video
            ref="video"
            :src="url(`/assets/animations/transform-home.mp4`)"
            playsinline
            autoplay
            muted
            loop
        />
        <div class="overlay" />
        <section>
            <Txt :size="viewport.is.mobile ? 32 : viewport.is.tablet ? 48 : 72" heading>
                Transform it.
            </Txt>
            <Txt size="l" class="description">
                Transform any document into beautiful, engaging Microlearning content in no time!
            </Txt>
            <Clickable
                data-placement="hero"
                @click="transform"
            >
                <Txt size="s" heading>
                    {{ viewport.is.tablet ? 'Watch the video' : 'Upload a document' }}
                </Txt>
                <Icon v-if="!viewport.is.mobile" name="arrow-right" />
            </Clickable>
            <Txt v-if="!viewport.is.tablet" size="s" class="subaction">
                You can also
                <a
                    href="/build"
                    data-placement="hero"
                >
                    try other create methods
                </a>
                or use our
                <a
                    href="/course-templates"
                    target="_blank"
                    @click="$track.event('Templates Catalog Opened', {
                        'button_cta': 'prebuilt templates',
                        'button_placement': 'hero'
                    })"
                >
                    templates
                </a>
            </Txt>
        </section>
    </div>
    <ClientOnly>
        <TransformFlow
            v-if="isTransforming"
            @close="isTransforming = false"
        />
    </ClientOnly>
    <ClientOnly>
        <Lightbox
            v-if="isWatchingVideo"
            @close="isWatchingVideo = false"
        >
            <Oembed src="https://vimeo.com/875382200" />
        </Lightbox>
    </ClientOnly>
</template>

<script>
definePageMeta({
    layout: 'cms',
});

import { url } from '@/helpers/utilities';
import { defineAsyncComponent } from 'vue';
import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Clickable from '@/components/utility/clickable';

const Oembed = defineAsyncComponent(() => import('@/components/media/oembed'));
const Lightbox = defineAsyncComponent(() => import('@/components/modals/lightbox'));
const TransformFlow = defineAsyncComponent(() => import('@/components/author/modals/transform'));

export default {
    name: 'TransformPage', 
    components: { Txt, Icon, Clickable, Oembed, Lightbox, TransformFlow },
    inject: ['$global'],
    data() {
        return {
            isTransforming: false,
            isWatchingVideo: false,
            viewport: { is: {} }
        };
    },
    watch: {
        'viewport.is': {
            handler() {
                if (this.viewport.is.tablet) {
                    const buttons = document.querySelectorAll('button, a');
                    buttons.forEach((button) => {
                        // Get second child element
                        const text = button?.children[0]?.children[0];
                        if (text && (text.innerText === 'Try it now' || text.innerText === 'Let\'s go')) {
                            text.innerText = 'Watch the video';
                        }
                    });
                }
            },
            deep: true,
            immediate: true
        }
    },
    mounted() {
        this.$track.baseProperties = {
            ...this.$track.baseProperties,
            'page_name': 'transform document to course',
            'page_type': 'marketing',
            'feature_availability': this.$global.isLoggedIn ? undefined : 'public'
        };
        this.viewport = useResponsive();
        this.$bus.on('cms:transform', this.transform);
        this.$bus.on('cms:transform-video', this.transformVideo);
    },
    methods: {
        url,
        transform({ target }) {
            if (target.tagName !== 'BUTTON') target = target.closest('button');
            if (this.viewport.is.tablet) {
                this.transformVideo({ target });
            } else {
                if (this.isTransforming) return;
                this.isTransforming = true;
                this.$track.event('Course Creation Started', {
                    'course_creation_type': 'transform',
                    'button_cta': target.innerText,
                    'button_placement': target.dataset.placement || 'hero'
                });
            }
        },
        transformVideo({ target }) {
            if (this.isWatchingVideo) return;
            if (target.tagName !== 'BUTTON') target = target.closest('button');
            this.isWatchingVideo = true;
            this.$track.event('Marketing Video Played', {
                'button_cta': target.innerText,
                'button_placement': target.dataset.placement || 'hero'
            });
        }
    }
};
</script>

<style lang="less" scoped>
.transform-hero.section-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 96px 48px 32px;
    margin: 24px;
    border-radius: 16px;
    position: relative;
    background: linear-gradient(90deg, #15206D 8.86%, #314DC3 68.12%);
    color: #FFF;
    video {
        position: absolute;
        bottom: 0;
        z-index: 0;

        width: 1500px;
        margin: auto;
    }
    .overlay {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        z-index: 1;

        width: 600px;
        height: 280px;
        margin: auto;
        background: radial-gradient(50% 50% at 50% 50%, rgba(15, 20, 61, 0.80) 0%, rgba(9, 12, 38, 0.00) 100%);
    }
    section {
        position: relative;
        z-index: 3;
        height: 540px;
        max-width: 480px;
        text-align: center;

        .description {
            opacity: 0.8;
            max-width: 400px;
        }
        .subaction {
            margin-top: auto;
            color: #8389AE;
            a {
                color: #FFF;
                text-decoration: underline;
            }
        }
        button {
            margin-top: 24px;
            width: 340px;
            max-width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 24px 36px;
            border-radius: 48px;
            background: linear-gradient(272deg, rgba(68, 108, 252, 0.70) 36.03%, rgba(157, 174, 236, 0.70) 97.25%);
            backdrop-filter: blur(4px);
            &:hover {
                background-color: var(--primary-shade-1);
                box-shadow: var(--primary-shadow);
            }
        }
    }
    &.mobile {
        padding: 96px 24px 16px;
        section {
            button { justify-content: center; }
        }
    }
}
</style>
